import React from 'react';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import ContactsPage from '../views/pages/ContactsPage';
import LoginPage from '../views/pages/auth/LoginPage';
import AuthenticatedRoute from './AuthenticatedRoute';
import NotFoundPage from '../views/pages/NotFoundPage';
import NavBar from '../views/components/layouts/NavBar';
import MagicLoginPage from '../views/pages/auth/MagicLoginPage';
import AddressLabelPage from '../views/pages/payment/AddressLabelPage';
import PaymentCompletionPage from '../views/pages/payment/PaymentCompletionPage';
import Footer from '../views/components/layouts/Footer';
import BusinessOverviewPage from '../views/pages/BusinessOverviewPage';
import SelectedBusinessRoute from './SelectedBusinessRoute';
import GeneralSettingsPage from '../views/pages/settings/GeneralSettingsPage';
import RequestedDataPage from '../views/pages/settings/RequestedDataPage';
import AcceptAdminInvitePage from '../views/pages/AcceptAdminInvitePage';
import useAuth from '../hooks/account/useAuth';
import ProfilePage from '../views/pages/ProfilePage';
import OnboardingPage from '../views/pages/flows/OnboardingPage';
import AboutPage from '../views/pages/info/AboutPage';
import ApplicationLayout from '../views/components/layouts/ApplicationLayout/ApplicationLayout';
import { isCommunity, isMoments } from '../constants';
import SharedDataPage from '../views/pages/settings/SharedDataPage';
import InviteLinkPage from '../views/pages/flows/InviteLinkPage';
import CreateEventPage from '../views/pages/agenda/CreateEventPage';
import EventDetailsPage from '../views/pages/agenda/EventDetailsPage';
import UploadExcelPage from '../views/pages/settings/UploadExcelPage';
import { SubscriptionsPage } from '../views/pages/payment/SubscriptionsPage';
import ExportExcelPage from '../views/pages/settings/ExportExcelPage';
import FeedbackPage from '../views/pages/FeedbackPage';
import IntegrationsPage from '../views/pages/IntegrationsPage';
import UpgradeSubscriptionPage from '../views/pages/payment/UpgradeSubscriptionPage';
import ManageSubscriptionPage from '../views/pages/settings/ManageSubscriptionPage';
import InvoicesPage from '../views/pages/payment/InvoicesPage';
import OverviewPage from '../views/pages/OverviewPage';
import RaceOverviewPage from '../views/pages/race/RaceOverviewPage';
import AssociationPage from '../views/pages/race/AssociationPage';
import ConfigureIntegrationsPage from '../views/pages/ConfigureIntegrationsPage';
import ScoreListsPage from '../views/pages/lists/ScoreListsPage';
import TaskOverviewPage from '../views/pages/lists/TaskOverviewPage';
import CreateCommunicationPage from '../views/pages/communication/CreateCommunicationPage';
import CommunicationsPage from '../views/pages/communication/CommunicationsPage';
import ActivateDashboardPage from '../views/pages/flows/ActivateDashboardPage';
import MutationsPage from '../views/pages/financial/MutationsPage';
import CompleteCardlessPage from '../views/pages/financial/CompleteCardlessPage';
import BulkSplittingPage from '../views/pages/financial/BulkSplittingPage';
import AccountingPage from '../views/pages/financial/AccountingPage';
import CategoriesPage from '../views/pages/financial/CategoriesPage';
import PotsPage from '../views/pages/financial/PotsPage';
import AffiliatePage from '../views/pages/AffiliatePage';
import CreateAffiliateCodePage from '../views/pages/flows/CreateAffiliateCodePage';
import ContributionPlanPage from '../views/pages/financial/ContributionPlanPage';
import PhotoAlbumsOverviewPage from '../views/pages/photo-albums/PhotoAlbumsOverview';
import PhotoAlbumPage from '../views/pages/photo-albums/PhotoAlbumPage';
import CreatePhotoAlbumPage from '../views/pages/flows/CreatePhotoAlbumPage';
import GeneralEventPage from '../views/pages/agenda/GeneralEventPage';
import FinancialMemberInformationPage from '../views/pages/financial/FinancialMemberInformationPage';
import MerchantPayoutPage from '../views/pages/financial/MerchantPayoutPage';
import MarketplaceOrdersPage from '../views/pages/financial/MarketplaceOrdersPage';
import FinancialOverviewPage from '../views/pages/financial/FinancialOverviewPage';
import AdministrationPage from '../views/pages/financial/AdministrationPage';
import DeclarationsPage from '../views/pages/financial/DeclarationsPage';
import SalesCollectPage from '../views/pages/financial/SalesCollectPage';
import ReportsPage from '../views/pages/financial/ReportsPage';
import FilesPage from '../views/pages/FilesPage';

export default function Router(): JSX.Element {
  const auth = useAuth();
  const location = useLocation().pathname;

  return (
    <>
      {(!auth || location === '/admin/invite') &&
        location !== '/onboarding' &&
        !location.startsWith('/race') && <NavBar />}
      <Routes>
        <Route path="login" element={<LoginPage />} />
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route path="magic" element={<MagicLoginPage />} />
        <Route path="/admin/invite" element={<AcceptAdminInvitePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/race" element={<RaceOverviewPage />} />
        <Route path="/race/association/:associationId" element={<AssociationPage />} />
        {!auth && <Route path="" element={<LoginPage />} />}
        <Route path="" element={<AuthenticatedRoute />}>
          <Route path="" element={<ApplicationLayout />}>
            <Route path="" element={<BusinessOverviewPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/dashboard/activate" element={<ActivateDashboardPage />} />
            <Route path="/integrations/manage" element={<ConfigureIntegrationsPage />} />
            <Route path="" element={<SelectedBusinessRoute />}>
              <Route path="/overview" element={<OverviewPage />} />
              <Route path="/contacts" element={<ContactsPage />} />
              <Route path="/settings" element={<GeneralSettingsPage />} />
              <Route path="/invite-link" element={<InviteLinkPage />} />
              <Route path="/settings/requested-data" element={<RequestedDataPage />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              {isMoments && <Route path="/settings/shared" element={<SharedDataPage />} />}
              <Route path="/settings/upload-excel" element={<UploadExcelPage />} />
              <Route path="/settings/export-excel" element={<ExportExcelPage />} />
              {isMoments && <Route path="/payment/address-label" element={<AddressLabelPage />} />}
              <Route path="/payment/completion" element={<PaymentCompletionPage />} />
              {isCommunity && (
                <>
                  <Route path="/settings/subscriptions" element={<SubscriptionsPage />} />
                  <Route path="/subscriptions/upgrade" element={<UpgradeSubscriptionPage />} />
                  <Route path="/subscriptions/manage" element={<ManageSubscriptionPage />} />
                  <Route path="/subscriptions/invoices" element={<InvoicesPage />} />
                  <Route path="/albums" element={<PhotoAlbumsOverviewPage />} />
                  <Route path="/create-album" element={<CreatePhotoAlbumPage />} />
                  <Route path="/album/:albumId" element={<PhotoAlbumPage />} />
                  <Route path="/files" element={<FilesPage />} />
                  <Route path="/affiliate" element={<AffiliatePage />} />
                  <Route path="/affiliate/create" element={<CreateAffiliateCodePage />} />
                  <Route path="/events" element={<GeneralEventPage />} />
                  {/* events */}
                  <Route path="/events" element={<GeneralEventPage />} />
                  <Route path="/events/create" element={<CreateEventPage />} />
                  <Route path="/events/details" element={<EventDetailsPage />} />

                  {/* Lists */}
                  <Route path="/lists/score" element={<ScoreListsPage />} />
                  <Route path="/lists/task" element={<TaskOverviewPage />} />

                  <Route path="/financial/overview" element={<FinancialOverviewPage />} />
                  <Route path="/financial/administration" element={<AdministrationPage />} />
                  <Route path="/financial/declarations" element={<DeclarationsPage />} />
                  <Route path="/financial/mutations/:bankAccountId" element={<MutationsPage />} />
                  <Route path="/financial/accounting" element={<AccountingPage />} />
                  <Route path="/financial/categories" element={<CategoriesPage />} />
                  <Route path="/financial/pots" element={<PotsPage />} />
                  <Route path="/financial/sales-collect" element={<SalesCollectPage />} />
                  <Route path="/financial/reports" element={<ReportsPage />} />
                  <Route path="/financial/bulk-split" element={<BulkSplittingPage />} />
                  <Route
                    path="/financial/overview/complete-go-cardless"
                    element={<CompleteCardlessPage />}
                  />
                  <Route
                    path="/financial/contribution-plans/:contributionPlanId"
                    element={<ContributionPlanPage />}
                  />
                  <Route
                    path="/financial/member/:connectionId"
                    element={<FinancialMemberInformationPage />}
                  />
                  <Route path="/financial/payout/:payoutId" element={<MerchantPayoutPage />} />
                  <Route path="/financial/orders" element={<MarketplaceOrdersPage />} />
                  {/* Redirects for old financial routes */}
                  <Route
                    path="/financial/mutations"
                    element={<Navigate to="/financial/administration" replace />}
                  />
                  <Route
                    path="/financial/transactions"
                    element={<Navigate to="/financial/administration" replace />}
                  />
                  <Route
                    path="/financial/member-balance"
                    element={<Navigate to="/financial/reports" replace />}
                  />
                  <Route
                    path="/financial/balance"
                    element={<Navigate to="/financial/reports" replace />}
                  />
                  <Route
                    path="/financial/contribution-plans"
                    element={<Navigate to="/financial/sales-collect" replace />}
                  />
                  <Route
                    path="/financial/merchantAccount"
                    element={<Navigate to="/financial/sales-collect" replace />}
                  />
                  <Route
                    path="/financial/products"
                    element={<Navigate to="/financial/sales-collect" replace />}
                  />
                </>
              )}
              <Route path="/communication" element={<CommunicationsPage />} />
              <Route path="/communication/create" element={<CreateCommunicationPage />} />
              <Route path="/communication/edit" element={<CreateCommunicationPage />} />
            </Route>
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
      {(!auth || location === '/admin/invite') &&
        location !== '/onboarding' &&
        !location.startsWith('/race') && <Footer />}
    </>
  );
}
